import { useState, useMemo, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ChevronLeft, ChevronRight, X, Save, FileText } from "lucide-react";
import { INSTALLATION_PHASES } from "@/utils/installationPhases";
import { InstallPhaseStep } from "@/components/admin/estimates/InstallPhaseStep";
import { EstimateSummaryPanel } from "@/components/admin/estimates/EstimateSummaryPanel";
import { MeasurementVariablesInput } from "@/components/admin/estimates/MeasurementVariablesInput";
import { useProducts } from "@/hooks/useProducts";
import { usePriceBooks } from "@/hooks/usePriceBooks";
import { useEstimateLineItems } from "@/hooks/useEstimateLineItems";
import { useEstimateCalculations } from "@/hooks/useEstimateCalculations";
import { usePhaseSettings } from "@/hooks/usePhaseSettings";
import { useCategoryConfig, getCategoryConfig } from "@/hooks/useCategoryConfig";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { 
  getSelectionRuleForProduct, 
  findConflictingLineItem, 
  canAddMoreOfProduct 
} from "@/utils/productSelectionRules";

export default function SalesPresentationMode() {
  const { estimateId } = useParams();
  const navigate = useNavigate();
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [showMeasurements, setShowMeasurements] = useState(true);
  const [showEstimateSummary, setShowEstimateSummary] = useState(true);
  const [selectedShingleType, setSelectedShingleType] = useState<string | null>(null);
  const hasGeneratedRef = useRef(false);
  const [measurementVars, setMeasurementVars] = useState({
    area_sqft: 0,
    eave_lf: 0,
    rake_lf: 0,
    valley_lf: 0,
    hip_lf: 0,
    ridge_lf: 0,
    sidewall_lf: 0,
    headwall_lf: 0,
    flashing_lf: 0,
    chimney_count: 0,
    skylight_count: 0,
    pipe_1_5_count: 0,
    pipe_3_count: 0,
    layers: 1,
  });

  const { products = [], isLoading: productsLoading } = useProducts();
  const { productPrices = [], isLoading: pricesLoading } = usePriceBooks();
  const { data: phaseSettings } = usePhaseSettings();
  const { data: categoryConfigs } = useCategoryConfig();
  const { lineItems, addLineItem, deleteLineItem, updateLineItem, replaceOrAddLineItem, addMultipleLineItems } = useEstimateLineItems(estimateId);
  const { subtotal, taxAmount, total } = useEstimateCalculations(lineItems || [], 8.625);

  // Fetch estimate details
  const { data: estimate, isLoading: isLoadingEstimate, error: estimateError } = useQuery({
    queryKey: ["estimate", estimateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estimates")
        .select("*")
        .eq("id", estimateId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!estimateId,
  });

  // Fetch measurements for this estimate
  const { data: measurements, isLoading: isLoadingMeasurements } = useQuery({
    queryKey: ["measurements", estimateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("measurements")
        .select("*")
        .eq("estimate_id", estimateId);

      if (error) throw error;
      return data;
    },
    enabled: !!estimateId,
  });

  // Auto-load measurements when they're fetched
  useEffect(() => {
    if (estimate?.id && !isLoadingMeasurements) {
      const existing = measurements?.find(m => m.estimate_id === estimate.id);
      if (existing) {
        setMeasurementVars({
          area_sqft: existing.area_sqft || 0,
          eave_lf: existing.eave_lf || 0,
          rake_lf: existing.rake_lf || 0,
          valley_lf: existing.valley_lf || 0,
          hip_lf: existing.hip_lf || 0,
          ridge_lf: existing.ridge_lf || 0,
          sidewall_lf: existing.wall_step_lf || 0,
          headwall_lf: existing.flashing_lf || 0,
          flashing_lf: existing.flashing_lf || 0,
          chimney_count: existing.chimney_count || 0,
          skylight_count: existing.skylight_count || 0,
          pipe_1_5_count: existing.pipe_count || 0,
          pipe_3_count: existing.fan_count || 0,
          layers: existing.layers || 1,
        });
      }
    }
  }, [estimate?.id, measurements, isLoadingMeasurements]);

  // Auto-generate estimate from TIER DEFAULTS
  useEffect(() => {
    if (!estimate || !estimate.price_book_id) return;
    
    // Prevent duplicate generation
    if (hasGeneratedRef.current) return;
    
    const existing = measurements?.find(m => m.estimate_id === estimate?.id);
    
    if (
      existing && 
      lineItems?.length === 0 && 
      !addMultipleLineItems.isPending
    ) {
      // Mark as initiated
      hasGeneratedRef.current = true;
      
      console.log("🚀 Generating estimate from tier defaults...");
      
      import('@/utils/estimateAutoGeneration').then(({ generateDefaultEstimateLineItems }) => {
        generateDefaultEstimateLineItems(
          estimate.id,
          measurementVars,
          estimate.price_book_id
        ).then((count) => {
          console.log(`📦 Generated ${count} tier default line items`);
          
          if (count > 0) {
            toast({
              title: "Estimate Generated",
              description: `${count} default tier products added automatically`,
            });
          } else {
            toast({
              title: "No Tier Defaults Configured",
              description: "Go to Tier Configuration to set up Good/Better/Best defaults",
              variant: "destructive",
            });
            hasGeneratedRef.current = false;
          }
        }).catch((error) => {
          hasGeneratedRef.current = false;
          toast({
            title: "Failed to generate estimate",
            description: error.message,
            variant: "destructive",
          });
        });
      });
    }
  }, [
    estimate?.id, 
    estimate?.price_book_id, 
    measurements?.length, 
    lineItems?.length,
    measurementVars.area_sqft, // Only generate when measurements are loaded
  ]);

  // Auto-select default product when entering a phase
  useEffect(() => {
    if (!currentPhase || !estimateId || !phaseSettings || !products || !measurements || !measurementVars.area_sqft) return;
    
    const phaseSetting = phaseSettings.find(ps => ps.phase_key === currentPhase.key);
    const defaultProductId = phaseSetting?.default_product_id;
    
    if (!defaultProductId) return;
    
    // Check if this phase already has selections
    const hasExistingSelection = lineItems?.some(item => {
      const itemProduct = products.find(p => p.id === item.product_id);
      return itemProduct?.install_phase === currentPhase.key;
    });
    
    // Don't auto-select if user already made a selection
    if (hasExistingSelection) return;
    
    // For Phase 11.5 (colors), only auto-select if shingle type is chosen
    if (currentPhase.key === 'Phase 11.5: Shingle Color' && !selectedShingleType) {
      return;
    }
    
    // Find the default product and its price
    const defaultProduct = products.find(p => p.id === defaultProductId);
    const defaultPrice = productPrices?.find(pp => 
      pp.product_id === defaultProductId && 
      pp.price_book_id === estimate?.price_book_id
    );
    
    if (defaultProduct && defaultPrice) {
      // Auto-select using the same logic as manual selection
      handleProductSelect(defaultProduct, defaultPrice.unit_price);
      toast({
        title: "Default Product Added",
        description: `${defaultProduct.name} has been automatically selected`,
      });
    }
  }, [currentPhaseIndex, phaseSettings, estimateId, measurements, measurementVars.area_sqft, selectedShingleType]);

  const currentPhase = INSTALLATION_PHASES[currentPhaseIndex];
  const progress = ((currentPhaseIndex + 1) / INSTALLATION_PHASES.length) * 100;

  // Filter products for current phase
  const phaseProducts = useMemo(() => {
    let filtered = products.filter(p => p.install_phase === currentPhase?.key) as any[];
    
    // Phase 11.5: Show only colors matching selected shingle type
    if (currentPhase?.key === 'Phase 11.5: Shingle Color') {
      if (!selectedShingleType) {
        return []; // No colors if no type selected
      }
      filtered = filtered.filter(p => 
        p.category === 'Shingle Colors' && 
        p.subcategory?.toLowerCase() === selectedShingleType.toLowerCase()
      );
    }
    
    return filtered;
  }, [products, currentPhase?.key, selectedShingleType]);

  // Track selected product IDs
  const selectedProductIds = useMemo(() => {
    return new Set((lineItems || []).map(item => item.product_id));
  }, [lineItems]);

  const handleProductSelect = async (product: any, price: number) => {
    if (!estimateId) {
      toast({
        title: "Error",
        description: "No estimate selected",
        variant: "destructive",
      });
      return;
    }

    const isSelected = selectedProductIds.has(product.id);

    // If deselecting, just remove
    if (isSelected) {
      const existingItem = lineItems?.find(item => item.product_id === product.id);
      if (existingItem?.id) {
        await deleteLineItem.mutateAsync(existingItem.id);
        
        // If removing shingle type (Phase 11), clear selected type and color
        if (currentPhase?.key === 'Phase 11: Shingle Field' && product.subcategory) {
          setSelectedShingleType(null);
          // Remove any color selection from Phase 11.5
          const colorItem = lineItems?.find(item => {
            const itemProduct = products.find(p => p.id === item.product_id);
            return itemProduct?.category === 'Shingle Colors';
          });
          if (colorItem?.id) {
            await deleteLineItem.mutateAsync(colorItem.id);
          }
        }
      }
      return;
    }

    // Check if we're replacing a product from the same phase (tier swap)
    const existingPhaseItem = lineItems?.find(item => {
      const itemProduct = products.find(p => p.id === item.product_id);
      return itemProduct?.install_phase === product.install_phase;
    });

    // Check if product can be added (handles mutual exclusivity and measurement limits)
    const conflictingItem = findConflictingLineItem(product, lineItems || []);
    const categoryConfig = getCategoryConfig(product.category, categoryConfigs);
    const { canAdd } = canAddMoreOfProduct(
      product, 
      lineItems || [], 
      measurementVars,
      categoryConfig
    );

    if (!canAdd && !existingPhaseItem) {
      // Allow replacement even if limit reached
      toast({
        title: "Cannot add product",
        description: "Selection limit reached",
        variant: "destructive",
      });
      return;
    }

    // Calculate quantity based on measurements and product type
    try {
      // Calculate quantity based on measurements and product type
      let quantity = 1;
      let calculationNote = '';
      const squares = measurementVars.area_sqft / 100;

      // Auto-calculate quantities
      if (product.category?.toLowerCase().includes('shingle') && !product.category?.toLowerCase().includes('ridge')) {
        quantity = squares * 1.1; // Add 10% waste
        calculationNote = `${measurementVars.area_sqft} sqft ÷ 100 × 1.1 waste = ${quantity.toFixed(2)} squares`;
      } else if (product.category?.toLowerCase().includes('underlayment')) {
        const sqftPerRoll = (product.specifications as any)?.coverage_per_unit || 1000;
        const totalSqftNeeded = measurementVars.area_sqft * 1.1;
        quantity = Math.ceil(totalSqftNeeded / sqftPerRoll);
        calculationNote = `${measurementVars.area_sqft} sqft × 1.1 waste = ${totalSqftNeeded.toFixed(0)} sqft ÷ ${sqftPerRoll} per roll`;
      } else if (product.category?.toLowerCase().includes('ice') || product.category?.toLowerCase().includes('water')) {
        const totalLF = (measurementVars.valley_lf || 0) + (measurementVars.eave_lf || 0) + (measurementVars.flashing_lf || 0);
        const rollCoverage = (product.specifications as any)?.roll_dimensions?.coverage || 99;
        quantity = Math.ceil((totalLF * 3) / rollCoverage);
        calculationNote = `${totalLF} LF × 3ft width = ${(totalLF * 3).toFixed(0)} sqft ÷ ${rollCoverage} per roll`;
      } else if (product.category?.toLowerCase().includes('ridge')) {
        const totalLF = (measurementVars.ridge_lf || 0) + (measurementVars.hip_lf || 0);
        quantity = Math.ceil(totalLF / 30); // Typical ridge cap coverage
        calculationNote = `${totalLF} LF (ridge + hip) ÷ 30 LF per bundle`;
      } else if (
        product.category?.toLowerCase().includes('gutter') && 
        !product.name?.toLowerCase().includes('downspout')
      ) {
        // Gutters: auto-populate from eave length
        quantity = measurementVars.eave_lf || 1;
        calculationNote = `${measurementVars.eave_lf} LF from measurements`;
      } else if (product.name?.toLowerCase().includes('downspout')) {
        // Downspouts: manual entry only (default to 1)
        quantity = 1;
      }

      const lineTotal = quantity * price;

      const newLineItem = {
        estimate_id: estimateId,
        product_id: product.id,
        description: product.name,
        quantity,
        unit: product.unit,
        unit_price: price,
        discount_percent: 0,
        discount_amount: 0,
        line_total: lineTotal,
        calculation_source: 'auto' as const,
        calculation_note: calculationNote || undefined,
      };

      // Track shingle type selection (Phase 11)
      if (currentPhase?.key === 'Phase 11: Shingle Field' && product.subcategory) {
        const oldType = selectedShingleType;
        setSelectedShingleType(product.subcategory);
        
        // If changing shingle type, remove any existing color selection
        if (oldType && oldType !== product.subcategory) {
          const colorItem = lineItems?.find(item => {
            const itemProduct = products.find(p => p.id === item.product_id);
            return itemProduct?.category === 'Shingle Colors';
          });
          if (colorItem?.id) {
            await deleteLineItem.mutateAsync(colorItem.id);
          }
        }
      }

      // If replacing existing phase item, delete it first (tier swap)
      if (existingPhaseItem?.id) {
        await deleteLineItem.mutateAsync(existingPhaseItem.id);
        console.log(`🔄 Replaced ${existingPhaseItem.description} with ${product.name}`);
      }

      // Add the product to the estimate
      await addLineItem.mutateAsync(newLineItem);
    } catch (error: any) {
      toast({
        title: "Failed to add product",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleUpdateLineItem = async (itemId: string, updates: any) => {
    try {
      await updateLineItem.mutateAsync({ 
        id: itemId,
        updates: {
          quantity: updates.quantity,
          unit_price: updates.unit_price,
          line_total: updates.line_total,
          calculation_source: updates.calculation_source || 'edited'
        }
      });
      toast({
        title: "Item updated",
        description: "Changes saved successfully",
      });
    } catch (error: any) {
      toast({
        title: "Failed to update item",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleTierChange = async (tier: 'good' | 'better' | 'best', phaseKey: string) => {
    console.log(`🎯 Switching to ${tier} tier for phase ${phaseKey}`);
    
    // The actual product swap is handled by InstallPhaseStep
    // This is just for tracking/analytics if needed
    
    toast({
      title: `Switched to ${tier.toUpperCase()} tier`,
      description: `Updated ${currentPhase?.title}`,
    });
  };

  const handlePrevious = () => {
    if (currentPhaseIndex > 0) {
      setCurrentPhaseIndex(currentPhaseIndex - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNext = () => {
    if (currentPhaseIndex < INSTALLATION_PHASES.length - 1) {
      setCurrentPhaseIndex(currentPhaseIndex + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleExit = () => {
    navigate(`/admin/estimates/builder/${estimateId}`);
  };

  // Comprehensive loading check
  if (productsLoading || pricesLoading || isLoadingEstimate || isLoadingMeasurements) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg font-semibold mb-2">Loading Sales Presentation</p>
          <div className="space-y-1 text-sm text-muted-foreground">
            {isLoadingEstimate && <p>✓ Loading estimate details...</p>}
            {isLoadingMeasurements && <p>✓ Loading measurements...</p>}
            {productsLoading && <p>✓ Loading products catalog...</p>}
            {pricesLoading && <p>✓ Loading pricing data...</p>}
          </div>
        </div>
      </div>
    );
  }

  // Error state handling
  if (estimateError || !estimate) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <div className="text-destructive mb-4 text-xl">⚠️ Error Loading Estimate</div>
          <p className="text-muted-foreground mb-4">
            {estimateError?.message || "Estimate not found"}
          </p>
          <Button onClick={() => navigate('/admin/estimates')}>
            Back to Estimates
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background border-b shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleExit}
                className="min-w-[44px] min-h-[44px]"
              >
                <X className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold">
                  Sales Presentation Mode
                </h1>
                <p className="text-sm text-muted-foreground">
                  Build estimate with customer in real-time
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setShowMeasurements(!showMeasurements)}
                className="min-w-[44px] min-h-[44px]"
              >
                {showMeasurements ? 'Hide' : 'Show'} Measurements
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowEstimateSummary(!showEstimateSummary)}
                className="min-w-[44px] min-h-[44px]"
              >
                {showEstimateSummary ? 'Hide' : 'Show'} Summary
              </Button>
              <Button
                onClick={handleExit}
                className="min-w-[44px] min-h-[44px]"
              >
                <Save className="w-4 h-4 mr-2" />
                Save & Exit
              </Button>
            </div>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column - Measurements (collapsible) */}
          {showMeasurements && (
            <div className="lg:col-span-3">
              <div className="lg:sticky lg:top-24">
              <MeasurementVariablesInput
                  values={measurementVars}
                  onChange={(values) => setMeasurementVars({ ...measurementVars, ...values })}
                />
              </div>
            </div>
          )}

          {/* Center Column - Phase Content */}
          <div className={cn(
            "transition-all duration-300",
            showMeasurements && showEstimateSummary ? "lg:col-span-6" :
            !showMeasurements && showEstimateSummary ? "lg:col-span-9" :
            showMeasurements && !showEstimateSummary ? "lg:col-span-9" :
            "lg:col-span-12"
          )}>
            <InstallPhaseStep
              phase={currentPhase}
              products={phaseProducts}
              productPrices={productPrices || []}
              selectedProductIds={selectedProductIds}
              onProductSelect={handleProductSelect}
              priceBookId={productPrices?.[0]?.price_book_id || ''}
              measurementVars={measurementVars}
              lineItems={lineItems || []}
              categoryConfigs={categoryConfigs || []}
              selectedShingleType={selectedShingleType}
              onSkipColorSelection={() => handleNext()}
              defaultProductId={phaseSettings?.find(ps => ps.phase_key === currentPhase?.key)?.default_product_id}
              estimateId={estimateId}
              onTierChange={handleTierChange}
            />

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                size="lg"
                onClick={handlePrevious}
                disabled={currentPhaseIndex === 0}
                className="min-w-[44px] min-h-[44px]"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Step {currentPhaseIndex + 1} of {INSTALLATION_PHASES.length}
              </span>
              {currentPhaseIndex === INSTALLATION_PHASES.length - 1 ? (
                <Button
                  size="lg"
                  onClick={() => navigate(`/admin/estimates/${estimateId}/proposal`)}
                  className="min-w-[44px] min-h-[44px]"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View Proposal
                </Button>
              ) : (
                <Button
                  size="lg"
                  onClick={handleNext}
                  className="min-w-[44px] min-h-[44px]"
                >
                  Next
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              )}
            </div>
          </div>

          {/* Right Column - Estimate Summary */}
          {showEstimateSummary && (
            <div className="lg:col-span-3">
              <div className="lg:sticky lg:top-24 h-auto lg:h-[calc(100vh-7rem)]">
                <EstimateSummaryPanel
                  className="h-[600px] lg:h-full"
                  lineItems={lineItems || []}
                  subtotal={subtotal}
                  taxAmount={taxAmount}
                  total={total}
                  taxRate={8.625}
                  onRemoveItem={(itemId) => deleteLineItem.mutate(itemId)}
                  onUpdateItem={handleUpdateLineItem}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
